import React, { useState, useEffect, useRef, useCallback } from "react";
import { useParams } from "react-router-dom";
import { db } from "../firebase";
import {
  collection,
  addDoc,
  query,
  orderBy,
  onSnapshot,
  doc,
  getDoc,
  updateDoc,
  serverTimestamp,
  limit,
  increment,
  writeBatch,
} from "firebase/firestore";
import {
  Box,
  TextField,
  Button,
  Typography,
  CircularProgress,
  IconButton,
  Link,
} from "@mui/material";
import SendIcon from "@mui/icons-material/Send";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import { isLoggedIn } from "../session";
import Header3 from "./Header3";
import { CONTENT_WIDTH } from "./Page";

function ChatBox() {
  const { chatId, carId } = useParams();
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [authUser, setAuthUser] = useState(null);
  const [chatData, setChatData] = useState(null);
  const [carData, setCarData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const messagesEndRef = useRef(null);
  const [isContracted, setIsContracted] = useState(false);
  const [loadingContract, setLoadingContract] = useState(false);

  const fetchAuthUser = useCallback(async () => {
    try {
      const user = await isLoggedIn();
      if (user) {
        const userDocRef = doc(db, "users", user.uid);
        const userDocSnapshot = await getDoc(userDocRef);
        if (userDocSnapshot.exists()) {
          const userData = userDocSnapshot.data();
          setAuthUser({
            uid: user.uid,
            displayName: userData.displayName || user.email,
            email: user.email,
          });
        } else {
          throw new Error("User document not found in Firestore");
        }
      } else {
        throw new Error("User not authenticated");
      }
    } catch (error) {
      setError("Failed to authenticate user");
    }
  }, []);

  useEffect(() => {
    fetchAuthUser();
  }, [fetchAuthUser]);

  useEffect(() => {
    if (!authUser || !chatId || !carId) return;

    const fetchChatAndCarData = async () => {
      try {
        const chatDoc = await getDoc(doc(db, "chats", chatId));
        if (chatDoc.exists()) {
          setChatData(chatDoc.data());
        } else {
          setError("Chat not found");
          return;
        }

        const carDoc = await getDoc(doc(db, "cars", carId));
        if (carDoc.exists()) {
          setCarData(carDoc.data());
        } else {
          setError("Car not found");
          return;
        }

        setIsLoading(false);
      } catch (error) {
        setError("Failed to load chat and car data");
      }
    };

    fetchChatAndCarData();
  }, [authUser, chatId, carId]);

  useEffect(() => {
    if (!chatId) return;

    const messagesRef = collection(db, "chats", chatId, "messages");
    const q = query(messagesRef, orderBy("timestamp", "desc"), limit(50));

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const newMessages = snapshot.docs
        .map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }))
        .reverse();
      setMessages(newMessages);
      messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }, (error) => {
      setError("Failed to load messages");
    });

    return () => unsubscribe();
  }, [chatId]);

  // Con el chat abierto, los mensajes recibidos quedan leídos y el contador vuelve a cero
  useEffect(() => {
    if (!authUser || !chatId) return;
    const unread = messages.filter((message) => message.senderId !== authUser.uid && !message.read);
    if (unread.length === 0) return;

    const batch = writeBatch(db);
    unread.forEach((message) => {
      batch.update(doc(db, "chats", chatId, "messages", message.id), { read: true });
    });
    batch.update(doc(db, "chats", chatId), { [`unreadCount.${authUser.uid}`]: 0 });
    batch.commit().catch(() => {
      // Se reintenta con el próximo mensaje
    });
  }, [messages, authUser, chatId]);

  const handleSendMessage = async () => {
    if (newMessage.trim() === "" || !authUser) return;

    const messagesRef = collection(db, "chats", chatId, "messages");
    const chatRef = doc(db, "chats", chatId);

    try {
      await addDoc(messagesRef, {
        content: newMessage.trim(),
        senderId: authUser.uid,
        senderDisplayName: authUser.displayName,
        timestamp: serverTimestamp(),
        read: false,
      });

      const recipientId = chatData.participants.find((id) => id !== authUser.uid);
      await updateDoc(chatRef, {
        lastMessage: newMessage.trim(),
        lastMessageTime: serverTimestamp(),
        lastSenderId: authUser.uid,
        lastSenderName: authUser.displayName,
        [`unreadCount.${recipientId}`]: increment(1),
      });

      setNewMessage("");
      messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    } catch (error) {
      setError("Failed to send message");
    }
  };

  const handleContract = async () => {
    setLoadingContract(true);

    try {
      const carRef = doc(db, "cars", carId);
      await updateDoc(carRef, { vendido: true });

      setIsContracted(true);
    } catch (error) {
      setError("Failed to update car status");
    } finally {
      setLoadingContract(false);
    }
  };

  const linkify = (text) => {
    const urlRegex = /(https?:\/\/[^\s]+)/g;
    return text.split(urlRegex).map((part, index) => 
      urlRegex.test(part) ? (
        <Link key={index} href={part} target="_blank" rel="noopener noreferrer">
          {part}
        </Link>
      ) : (
        part
      )
    );
  };

  const handleKeyPress = (event) => {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      handleSendMessage();
    }
  };

  if (isLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <CircularProgress color="secondary" />
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <Typography variant="h6" color="error">
          {error}
        </Typography>
      </Box>
    );
  }

  const isSeller = carData.uid === authUser.uid;
  const column = { width: "100%", maxWidth: CONTENT_WIDTH, mx: "auto", px: { xs: 2, sm: 3 }, boxSizing: "border-box" };

  return (
    <Box
      sx={{
        bgcolor: "background.default",
        height: "100vh",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Header3
        subtitle={carData.price ? `$ ${carData.price}` : undefined}
        actions={
          isSeller && !isContracted && (
            <Button
              variant="outlined"
              color="primary"
              size="small"
              startIcon={<CheckCircleOutlineIcon />}
              onClick={handleContract}
              disabled={loadingContract}
              sx={{ flexShrink: 0 }}
            >
              {loadingContract ? "Procesando..." : "Marcar como vendido"}
            </Button>
          )
        }
      />
      <Box sx={{ flexGrow: 1, overflowY: "auto" }}>
        <Box sx={{ ...column, py: 3, display: "flex", flexDirection: "column", gap: 1.5 }}>
          {messages.length === 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: "center", mt: 4 }}>
              Escribí el primer mensaje para empezar la conversación.
            </Typography>
          )}
          {messages.map((message) => {
            const isMine = message.senderId === authUser.uid;
            return (
              <Box
                key={message.id}
                sx={{
                  alignSelf: isMine ? "flex-end" : "flex-start",
                  maxWidth: { xs: "85%", md: "65%" },
                  display: "flex",
                  flexDirection: "column",
                  alignItems: isMine ? "flex-end" : "flex-start",
                }}
              >
                {!isMine && (
                  <Typography variant="caption" sx={{ fontWeight: 700, color: "secondary.dark", mb: 0.5, ml: 1 }}>
                    {message.senderDisplayName}
                  </Typography>
                )}
                <Box
                  sx={{
                    px: 2,
                    py: 1.25,
                    borderRadius: 3,
                    borderBottomRightRadius: isMine ? 6 : 24,
                    borderBottomLeftRadius: isMine ? 24 : 6,
                    bgcolor: isMine ? "primary.main" : "background.paper",
                    color: isMine ? "primary.contrastText" : "text.primary",
                    border: isMine ? "none" : 1,
                    borderColor: "divider",
                    wordBreak: "break-word",
                    "& a": { color: isMine ? "secondary.main" : "secondary.dark" },
                  }}
                >
                  <Typography variant="body1">{linkify(message.content)}</Typography>
                </Box>
                {isMine && (
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, mr: 1 }}>
                    {message.read ? "Leído" : "Enviado"}
                  </Typography>
                )}
              </Box>
            );
          })}
          <div ref={messagesEndRef} />
        </Box>
      </Box>
      <Box sx={{ bgcolor: "background.paper", borderTop: 1, borderColor: "divider" }}>
        <Box sx={{ ...column, py: 1.5, display: "flex", alignItems: "flex-end", gap: 1 }}>
          <TextField
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Escribe tu mensaje..."
            variant="outlined"
            size="small"
            fullWidth
            multiline
            maxRows={4}
          />
          <IconButton
            aria-label="Enviar"
            onClick={handleSendMessage}
            disabled={!newMessage.trim()}
            sx={{
              bgcolor: "secondary.main",
              color: "secondary.contrastText",
              width: 40,
              height: 40,
              flexShrink: 0,
              "&:hover": { bgcolor: "secondary.dark" },
              "&.Mui-disabled": { bgcolor: "divider" },
            }}
          >
            <SendIcon fontSize="small" />
          </IconButton>
        </Box>
      </Box>
    </Box>
  );
}

export default ChatBox;
